import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Button from '../../../../components/ui/Button';
import { API_ENDPOINTS, API_BASE_URL } from '../../../../utils/config';
import { getContactId, getCustomerDisplayName, extractCustomerDetails, extractClientDetails } from '../utils/contactHelpers';
import { getBillToFromInvoice, getInvoiceLineItems } from '../utils/documentModalHelpers';
import { 
  FileCheck, 
  Search, 
  Eye,
  Edit,
  Trash2,
  CheckCircle,
  Clock,
  AlertCircle,
  X,
  Send,
  Plus,
  Repeat,
  Calendar
} from 'lucide-react';

const Quotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [ordering, setOrdering] = useState('-total_amount');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState(null);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [resolvedDetailsCustomer, setResolvedDetailsCustomer] = useState(null);
  const [resolvedDetailsClient, setResolvedDetailsClient] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editQuote, setEditQuote] = useState(null);
  const [editForm, setEditForm] = useState({
    status: 'draft',
    customer: '',
    invoice_number: '',
    invoice_date: '',
    due_date: '',
    subtotal: '',
    tax_amount: '',
    total_amount: '',
    notes: '',
    terms: '',
    customer_name: '',
    customer_company: '',
    customer_address: '',
    customer_gstin: '',
    customer_pan: '',
    items: []
  });
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [editCustomers, setEditCustomers] = useState([]);
  const [editClients, setEditClients] = useState([]);
  const [editContactType, setEditContactType] = useState('');
  const [companyProfile, setCompanyProfile] = useState(null);

  const getCompanyLogoUrl = (profileData) => {
    if (!profileData) return null;
    const rawLogo = profileData.company_logo || profileData.logo;
    if (!rawLogo) return null;
    const logoUrl = String(rawLogo);
    if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) return logoUrl;
    const baseUrl = (API_BASE_URL || '').replace('/api', '');
    return logoUrl.startsWith('/') ? `${baseUrl}${logoUrl}` : `${baseUrl}/${logoUrl}`;
  };

  const numberToWords = (num) => {
    if (!num || num === 0) return 'Zero';
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const convertHundreds = (n) => {
      if (n === 0) return '';
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convertHundreds(n % 100) : '');
    };
    const convert = (n) => {
      if (n === 0) return '';
      if (n < 1000) return convertHundreds(n);
      if (n < 100000) {
        const thousands = Math.floor(n / 1000);
        const remainder = n % 1000;
        return convertHundreds(thousands) + ' Thousand' + (remainder ? ' ' + convertHundreds(remainder) : '');
      }
      if (n < 10000000) {
        const lakhs = Math.floor(n / 100000);
        const remainder = n % 100000;
        return convert(lakhs) + ' Lakh' + (remainder ? ' ' + convert(remainder) : '');
      }
      const crores = Math.floor(n / 10000000);
      const remainder = n % 10000000;
      return convert(crores) + ' Crore' + (remainder ? ' ' + convert(remainder) : '');
    };
    const integerPart = Math.floor(num);
    return convert(integerPart) || 'Zero';
  };

  const fetchCompanyProfile = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.COMPANY_PROFILE_LIST, { headers: getAuthHeaders() });
      const profiles = Array.isArray(response.data) ? response.data : (response.data?.results || []);
      if (!profiles.length) { setCompanyProfile(null); return; }
      const p = profiles[0];
      setCompanyProfile({
        name: p.company_name || p.companyName || '',
        address: p.address || '',
        gstin: p.gstin || p.gst_in || '',
        phone: p.phone_number || p.phone || '',
        email: p.email || '',
        accountName: p.account_name || p.accountName || '',
        bankName: p.bank_name || p.bankName || '',
        accountNo: p.account_no || p.accountNo || p.account_number || '',
        ifscCode: p.ifsc_code || p.ifscCode || '',
        branch: p.branch || '',
        logoUrl: getCompanyLogoUrl(p)
      });
    } catch (err) {
      setCompanyProfile(null);
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.CUSTOMERS_LIST, { headers: getAuthHeaders() });
      const data = response.data;
      const normalized = Array.isArray(data) ? data : (data.results || data.data || []);
      setEditCustomers(normalized);
      return normalized;
    } catch (error) {
      return [];
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.CLIENTS_LIST, { headers: getAuthHeaders() });
      const data = response.data;
      const normalized = Array.isArray(data) ? data : (data.results || data.data || data.clients || []);
      setEditClients(normalized);
      return normalized;
    } catch (error) {
      return [];
    }
  };

  const fetchQuotes = async (page = 1) => {
    setIsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (searchTerm) params.append('search', searchTerm);
      params.append('page', page);
      params.append('ordering', ordering || '-total_amount');

      const response = await axios.get(
        `${API_ENDPOINTS.QUOTES_LIST}?${params.toString()}`,
        { headers: getAuthHeaders() }
      );

      const data = response.data;
      const normalized = Array.isArray(data) ? data : (data.results || []);
      const normalizedCount = Array.isArray(data) ? data.length : (data.count || normalized.length || 0);

      setQuotes(normalized);
      setTotalCount(normalizedCount);
      setNextPage(Array.isArray(data) ? null : data.next);
      setPrevPage(Array.isArray(data) ? null : data.previous);
      setCurrentPage(page);
    } catch (error) {
      const apiError = error?.response?.data?.message || 
                      error?.response?.data?.detail || 
                      error?.message || 
                      'Failed to fetch quotes';
      setError(apiError);
      setQuotes([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes(1);
  }, [filterStatus, ordering]);

  useEffect(() => {
    if (isDetailsOpen || isEditOpen) {
      fetchCompanyProfile();
    }
  }, [isDetailsOpen, isEditOpen]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchTerm !== '' || currentPage === 1) {
        fetchQuotes(1);
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  useEffect(() => {
    if (!isDetailsOpen || !selectedQuote) {
      setResolvedDetailsCustomer(null);
      setResolvedDetailsClient(null);
      return;
    }

    let cancelled = false;
    const headers = getAuthHeaders();
    const inv = selectedQuote;
    const rawCustomer = inv.customer;
    const rawClient = inv.client;
    const customerId =
      rawCustomer != null && typeof rawCustomer !== 'object' ? rawCustomer : null;
    const clientId =
      rawClient != null && typeof rawClient !== 'object' ? rawClient : null;

    const fetchContacts = async () => {
      let cust = null;
      let cli = null;
      if (customerId != null && customerId !== '') {
        try {
          const r = await axios.get(API_ENDPOINTS.CUSTOMERS_DETAIL(customerId), { headers });
          if (!cancelled) cust = r.data;
        } catch {
          /* keep flat / nested fields from quote */
        }
      }
      if (clientId != null && clientId !== '') {
        try {
          const r = await axios.get(API_ENDPOINTS.CLIENTS_DETAIL(clientId), { headers });
          if (!cancelled) cli = r.data;
        } catch {
          /* keep flat fields */
        }
      }
      if (!cancelled) {
        setResolvedDetailsCustomer(cust);
        setResolvedDetailsClient(cli);
      }
    };

    fetchContacts();
    return () => {
      cancelled = true;
    };
  }, [isDetailsOpen, selectedQuote?.id, selectedQuote?.customer, selectedQuote?.client]);

  const detailsBillTo = useMemo(
    () =>
      selectedQuote
        ? getBillToFromInvoice(selectedQuote, resolvedDetailsCustomer, resolvedDetailsClient)
        : { name: '', company: '', address: '', gstin: '', pan: '' },
    [selectedQuote, resolvedDetailsCustomer, resolvedDetailsClient]
  );

  const quoteLineItems = useMemo(
    () => getInvoiceLineItems(selectedQuote),
    [selectedQuote]
  );

  const handleViewQuote = async (quote) => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        API_ENDPOINTS.QUOTES_DETAIL(quote.id),
        { headers: getAuthHeaders() }
      );
      setSelectedQuote(response.data);
      setIsDetailsOpen(true);
    } catch (error) {
      const apiError = error?.response?.data?.message || 
                      error?.response?.data?.detail || 
                      error?.message || 
                      'Failed to fetch quote details';
      setError(apiError);
    } finally {
      setIsLoading(false);
    }
  };

  const openDeleteConfirm = (quote) => {
    setQuoteToDelete(quote);
    setConfirmDeleteOpen(true);
  };

  const handleOpenEdit = async (quote) => {
    try {
      setIsEditLoading(true);
      // Fetch customers/clients and quote details in parallel so dropdowns are populated
      const [custList, clientList, quoteResponse] = await Promise.all([
        fetchCustomers(),
        fetchClients(),
        axios.get(API_ENDPOINTS.QUOTES_DETAIL(quote.id), { headers: getAuthHeaders() })
      ]);
      const data = quoteResponse.data || {};
      setEditQuote(data);
      const contactTypeInit = data.customer ? 'customer' : (data.client ? 'client' : '');
      setEditContactType(contactTypeInit);
      const customerId = getContactId(data.customer) || getContactId(data.client);
      let customerDetails = { name: '', company: '', address: '', gstin: '', pan: '' };
      // Prefer denormalized fields from API, then extract from nested customer/client object, then lookup from list
      const fromDenorm = data.customer_name || data.customer_company || data.customer_address || data.client_name || data.client_company;
      if (fromDenorm) {
        customerDetails = {
          name: data.customer_name || data.client_name || '',
          company: data.customer_company || data.client_company || '',
          address: data.customer_address || data.client_address || '',
          gstin: data.customer_gstin || data.client_gstin || '',
          pan: data.customer_pan || data.client_pan || ''
        };
      } else if (typeof data.customer === 'object' && data.customer) {
        customerDetails = extractCustomerDetails(data.customer);
      } else if (typeof data.client === 'object' && data.client) {
        customerDetails = extractClientDetails(data.client);
      } else if (customerId && contactTypeInit === 'customer') {
        const c = custList.find((x) => String(x.id) === customerId);
        if (c) customerDetails = extractCustomerDetails(c);
      } else if (customerId && contactTypeInit === 'client') {
        const c = clientList.find((x) => String(x.id) === customerId);
        if (c) customerDetails = extractClientDetails(c);
      }
      setEditForm({
        status: data.status || 'draft',
        customer: customerId,
        invoice_number: data.invoice_number || '',
        invoice_date: data.invoice_date ? data.invoice_date.split('T')[0] : '',
        due_date: data.due_date ? data.due_date.split('T')[0] : '',
        subtotal: data.subtotal || '',
        tax_amount: data.tax_amount || '',
        total_amount: data.total_amount || '',
        notes: data.notes || '',
        terms: data.terms || '',
        customer_name: customerDetails.name,
        customer_company: customerDetails.company,
        customer_address: customerDetails.address,
        customer_gstin: customerDetails.gstin,
        customer_pan: customerDetails.pan,
        items: getInvoiceLineItems(data).map((it, idx) => {
          const code = it.sac_code || it.hsn_code || '';
          return {
            id: it.id || idx + 1,
            sac_code: code,
            hsn_code: code,
            description: it.description || '',
            quantity: it.quantity ?? '',
            rate: it.rate ?? '',
            amount: it.amount ?? '',
            tax_rate: it.tax_rate ?? '',
            tax_amount: it.tax_amount ?? ''
          };
        })
      });
      setIsEditOpen(true);
    } catch (error) {
      const apiError = error?.response?.data?.message || 
                      error?.response?.data?.detail || 
                      error?.message || 
                      'Failed to load quote for editing';
      setError(apiError);
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editQuote?.id) return;
    setIsEditLoading(true);
    try {
      const payload = {
        status: editForm.status,
        customer: editContactType === 'customer' && editForm.customer ? parseInt(editForm.customer) : null,
        client: editContactType === 'client' && editForm.customer ? parseInt(editForm.customer) : null,
        invoice_number: editForm.invoice_number || null,
        invoice_date: editForm.invoice_date || null,
        due_date: editForm.due_date || null,
        subtotal: editForm.subtotal?.toString() || '0',
        tax_amount: editForm.tax_amount?.toString() || '0',
        total_amount: editForm.total_amount?.toString() || '0',
        notes: editForm.notes,
        terms: editForm.terms,
        customer_name: editForm.customer_name || null,
        customer_company: editForm.customer_company || null,
        customer_address: editForm.customer_address || null,
        customer_gstin: editForm.customer_gstin || null,
        customer_pan: editForm.customer_pan || null,
        items: (editForm.items || []).map((it) => ({
          sac_code: it.sac_code || it.hsn_code || '',
          hsn_code: it.hsn_code || it.sac_code || '',
          description: it.description || '',
          quantity: it.quantity ?? 1,
          rate: it.rate ?? 0,
          amount: it.amount ?? 0,
          tax_rate: it.tax_rate ?? 0,
          tax_amount: it.tax_amount ?? 0
        }))
      };
      await axios.put(
        API_ENDPOINTS.QUOTES_UPDATE(editQuote.id),
        payload,
        { headers: getAuthHeaders() }
      );
      setSuccessMessage('Quote updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      setIsEditOpen(false);
      setEditQuote(null);
      fetchQuotes(currentPage);
    } catch (error) {
      const apiError = error?.response?.data?.message || 
                      error?.response?.data?.detail || 
                      error?.message || 
                      'Failed to update quote';
      setError(apiError);
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleEditItemChange = (index, field, value) => {
    setEditForm((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, items };
    });
  };

  const handleAddEditItem = () => {
    setEditForm((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          id: prev.items.length + 1,
          sac_code: '',
          hsn_code: '',
          description: '',
          quantity: 1,
          rate: '',
          amount: '',
          tax_rate: '',
          tax_amount: ''
        }
      ]
    }));
  };

  const handleRemoveEditItem = (index) => {
    setEditForm((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleDeleteQuote = async () => {
    if (!quoteToDelete) return;

    try {
      const response = await axios.delete(
        API_ENDPOINTS.QUOTES_DELETE(quoteToDelete.id),
        { headers: getAuthHeaders() }
      );
      const msg = response?.data?.message || 'Quote deleted successfully';
      setSuccessMessage(msg);
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchQuotes(currentPage);
    } catch (error) {
      const apiError = error?.response?.data?.message || 
                      error?.response?.data?.detail || 
                      error?.message || 
                      'Failed to delete quote';
      setError(apiError);
    } finally {
      setConfirmDeleteOpen(false);
      setQuoteToDelete(null);
    }
  };

  const handleConvertToInvoice = async (quote) => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        API_ENDPOINTS.QUOTES_CONVERT_TO_INVOICE(quote.id),
        {},
        { headers: getAuthHeaders() }
      );
      const msg = response?.data?.message || 'Quote converted to invoice';
      setSuccessMessage(msg);
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchQuotes(currentPage);
    } catch (error) {
      const apiError = error?.response?.data?.message || 
                      error?.response?.data?.detail || 
                      error?.message || 
                      'Failed to convert quote to invoice';
      setError(apiError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkAsAccepted = async (quote) => {
    try {
      await axios.post(
        API_ENDPOINTS.QUOTES_MARK_ACCEPTED(quote.id),
        {},
        { headers: getAuthHeaders() }
      );
      setSuccessMessage('Quote marked as accepted');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchQuotes(currentPage);
    } catch (error) {
      const apiError = error?.response?.data?.message || 
                      error?.response?.data?.detail || 
                      error?.message || 
                      'Failed to mark quote as accepted';
      setError(apiError);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'sent':
        return 'bg-blue-100 text-blue-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'accepted':
        return <CheckCircle className="w-4 h-4" />;
      case 'sent':
        return <Send className="w-4 h-4" />;
      case 'draft':
        return <FileCheck className="w-4 h-4" />;
      case 'rejected':
        return <X className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const filteredQuotes = quotes;

  return (
    <div className="space-y-6">
      {/* Notifications */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            <span>{error}</span>
          </div>
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Quote Details Modal - Same design as quote creation form */}
      {isDetailsOpen && selectedQuote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[95vh] flex flex-col my-4 sm:my-8 relative overflow-hidden">
            <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between flex-shrink-0 sticky top-0 z-[100] rounded-t-lg shadow-sm">
              <h2 className="text-lg sm:text-xl font-semibold text-foreground">Quote Details</h2>
              <button onClick={() => { setIsDetailsOpen(false); setSelectedQuote(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 overflow-y-auto flex-1">
              {/* Company Header - matches creation form */}
              <div className="border-b-2 border-gray-300 pb-4">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                  <div className="flex-1 w-full">
                    <h1 className="text-xl sm:text-2xl font-bold text-foreground mb-2">
                      {companyProfile?.name || 'COMPANY NAME'}
                    </h1>
                    <div className="text-xs sm:text-sm text-gray-600 space-y-1">
                      <p>Address: {companyProfile?.address || 'Company Address'}</p>
                      <p>Mobile: {companyProfile?.phone || '—'} | Email: {companyProfile?.email || '—'}</p>
                      <p>GSTIN: {companyProfile?.gstin || '—'}</p>
                    </div>
                  </div>
                  <div className="text-left sm:text-right w-full sm:w-auto">
                    {companyProfile?.logoUrl ? (
                      <img src={companyProfile.logoUrl} alt="Company" className="h-12 sm:h-16 object-contain ml-auto" />
                    ) : (
                      <div className="text-base sm:text-lg font-semibold text-primary">LOGO</div>
                    )}
                  </div>
                </div>
              </div>

              {/* QUOTE Banner */}
              <div className="bg-foreground text-white text-center py-2 sm:py-3 font-bold text-base sm:text-lg">QUOTE</div>

              {/* Document Details + Bill To - same 2-col layout as creation */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Quote No</p>
                    <p className="font-semibold">{selectedQuote.invoice_number || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Quote Date</p>
                    <p className="font-semibold">{selectedQuote.invoice_date ? new Date(selectedQuote.invoice_date).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Due Date</p>
                    <p className="font-semibold">{selectedQuote.due_date ? new Date(selectedQuote.due_date).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Status</p>
                    <p className="font-semibold capitalize">{selectedQuote.status || 'N/A'}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-foreground">Bill To</h3>
                  <p className="text-sm font-medium text-gray-700 mb-1">Name</p>
                  <p className="font-semibold">{detailsBillTo.name || 'N/A'}</p>
                  <p className="text-sm font-medium text-gray-700 mb-1">Company</p>
                  <p className="font-semibold">{detailsBillTo.company || 'N/A'}</p>
                  <p className="text-sm font-medium text-gray-700 mb-1">Address</p>
                  <p className="font-semibold">{detailsBillTo.address || 'N/A'}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">GSTIN</p>
                      <p className="font-semibold">{detailsBillTo.gstin || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">PAN</p>
                      <p className="font-semibold">{detailsBillTo.pan || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Services — line keys normalized via getInvoiceLineItems; empty state if API omits lines */}
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-foreground mb-3">Services</h3>
                {quoteLineItems.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-[640px] sm:min-w-full w-full border-collapse border border-gray-300">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="border border-gray-300 px-4 py-2 text-left text-xs sm:text-sm font-medium">S.No</th>
                          <th className="border border-gray-300 px-4 py-2 text-left text-xs sm:text-sm font-medium">SAC / HSN Code</th>
                          <th className="border border-gray-300 px-4 py-2 text-left text-xs sm:text-sm font-medium min-w-[150px]">Description of Goods / Services</th>
                          <th className="border border-gray-300 px-4 py-2 text-left text-xs sm:text-sm font-medium">Quantity</th>
                          <th className="border border-gray-300 px-4 py-2 text-left text-xs sm:text-sm font-medium">Rate (₹)</th>
                          <th className="border border-gray-300 px-4 py-2 text-left text-xs sm:text-sm font-medium">Amount (₹)</th>
                          <th className="border border-gray-300 px-4 py-2 text-left text-xs sm:text-sm font-medium">Tax Rate</th>
                          <th className="border border-gray-300 px-4 py-2 text-left text-xs sm:text-sm font-medium">Tax Amount (₹)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {quoteLineItems.map((item, idx) => (
                          <tr key={item.id || idx}>
                            <td className="border border-gray-300 px-4 py-2 text-sm">{idx + 1}</td>
                            <td className="border border-gray-300 px-4 py-2 text-sm">{item.sac_code || item.hsn_code || '—'}</td>
                            <td className="border border-gray-300 px-4 py-2 text-sm">{item.description || '—'}</td>
                            <td className="border border-gray-300 px-4 py-2 text-sm">{item.quantity ?? '—'}</td>
                            <td className="border border-gray-300 px-4 py-2 text-sm">₹{parseFloat(item.rate ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td className="border border-gray-300 px-4 py-2 text-sm">₹{parseFloat(item.amount ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td className="border border-gray-300 px-4 py-2 text-sm">{item.tax_rate != null ? `${item.tax_rate}%` : '—'}</td>
                            <td className="border border-gray-300 px-4 py-2 text-sm">₹{parseFloat(item.tax_amount ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600 border border-gray-200 rounded-md p-4 bg-gray-50">
                    No line items returned for this quote (check that the quote detail API includes saved lines).
                  </p>
                )}
              </div>

              {/* Amount Summary - matches creation form */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center gap-2">
                      <span className="text-sm text-gray-700">Total Taxable Value:</span>
                      <span className="text-sm font-semibold">₹{parseFloat(selectedQuote.subtotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between items-center gap-2">
                      <span className="text-sm text-gray-700">Tax Amount:</span>
                      <span className="text-sm font-semibold">₹{parseFloat(selectedQuote.tax_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between items-center gap-2 pt-2 border-t border-gray-300">
                      <span className="font-semibold">Total Amount:</span>
                      <span className="font-semibold">₹{parseFloat(selectedQuote.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="pt-2 border-t border-gray-300">
                      <p className="text-xs text-gray-600">Total Amount (in words): {numberToWords(Math.floor(parseFloat(selectedQuote.total_amount || 0)))} Rupees Only</p>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Signature</h3>
                  <div className="text-sm text-gray-600">
                    <p>For {companyProfile?.name || 'COMPANY NAME'}</p>
                    <div className="mt-8 border-t border-gray-300 pt-2">
                      <p>Authorized Signatory</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes & Terms - matches creation form */}
              {selectedQuote.notes && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                  <p className="text-sm text-gray-600 border border-gray-200 rounded-md p-3 bg-gray-50">{selectedQuote.notes}</p>
                </div>
              )}
              {selectedQuote.terms && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Terms</label>
                  <p className="text-sm text-gray-600 border border-gray-200 rounded-md p-3 bg-gray-50">{selectedQuote.terms}</p>
                </div>
              )}

              <div className="flex justify-end pt-4 border-t border-gray-200">
                <Button variant="outline" onClick={() => { setIsDetailsOpen(false); setSelectedQuote(null); }}>Close</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md flex items-center justify-between">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 mr-2" />
            <span>{successMessage}</span>
          </div>
          <button onClick={() => setSuccessMessage('')} className="text-green-600 hover:text-green-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Quotes</p>
              <p className="text-2xl font-bold text-foreground">{totalCount}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <FileCheck className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Draft</p>
              <p className="text-2xl font-bold text-foreground">
                {quotes.filter(q => q.status === 'draft').length}
              </p>
            </div>
            <div className="p-3 bg-gray-100 rounded-full">
              <FileCheck className="w-6 h-6 text-gray-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Sent</p>
              <p className="text-2xl font-bold text-foreground">
                {quotes.filter(q => q.status === 'sent').length}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <Send className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Accepted</p>
              <p className="text-2xl font-bold text-foreground">
                {quotes.filter(q => q.status === 'accepted').length}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search quotes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
          </select>
          <select
            value={ordering}
            onChange={(e) => setOrdering(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="-total_amount">Amount: High to Low</option>
            <option value="total_amount">Amount: Low to High</option>
            <option value="-invoice_date">Date: Newest first</option>
            <option value="invoice_date">Date: Oldest first</option>
          </select>
        </div>
      </div>

      {/* Quotes Table */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">Loading quotes...</p>
            </div>
          ) : filteredQuotes.length === 0 ? (
            <div className="p-12 text-center">
              <FileCheck className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-muted-foreground">No quotes found</p>
            </div>
          ) : (
            <>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quote</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer / Client</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredQuotes.map((quote) => (
                    <tr key={quote.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center mr-3">
                            <FileCheck className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{quote.invoice_number || 'N/A'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {quote.customer_name || quote.client_name || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">₹{parseFloat(quote.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {quote.invoice_date ? new Date(quote.invoice_date).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {quote.due_date ? new Date(quote.due_date).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(quote.status)}`}>
                          {getStatusIcon(quote.status)}
                          <span className="ml-1 capitalize">{quote.status || 'N/A'}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleViewQuote(quote)}
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {quote.status !== 'accepted' && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleConvertToInvoice(quote)}
                              title="Convert to Invoice"
                            >
                              <Repeat className="w-4 h-4" />
                            </Button>
                          )}
                          {quote.status === 'sent' && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleMarkAsAccepted(quote)}
                              title="Mark as Accepted"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleOpenEdit(quote)}
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => openDeleteConfirm(quote)}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {(nextPage || prevPage) && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing page {currentPage} of {Math.ceil(totalCount / 10)}
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchQuotes(currentPage - 1)}
                      disabled={!prevPage}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchQuotes(currentPage + 1)}
                      disabled={!nextPage}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {/* Delete Confirmation Modal */}
      {confirmDeleteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-foreground">Delete Quote</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Are you sure you want to delete{' '}
                <span className="font-semibold text-foreground">
                  {quoteToDelete?.invoice_number || 'this quote'}
                </span>
                ? This action cannot be undone.
              </p>
            </div>
            <div className="px-6 py-4 flex items-center justify-end gap-3">
              <Button variant="outline" onClick={() => { setConfirmDeleteOpen(false); setQuoteToDelete(null); }}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDeleteQuote}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Quote Modal - Same design as quote creation form */}
      {isEditOpen && editQuote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[95vh] flex flex-col my-4 sm:my-8 relative overflow-hidden">
            <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between flex-shrink-0 sticky top-0 z-[100] rounded-t-lg shadow-sm">
              <h2 className="text-lg sm:text-xl font-semibold text-foreground">Edit Quote</h2>
              <button onClick={() => { setIsEditOpen(false); setEditQuote(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>
            <form onSubmit={handleEditSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-6 overflow-y-auto flex-1">
              {/* Company Header - matches creation form */}
              <div className="border-b-2 border-gray-300 pb-4">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                  <div className="flex-1 w-full">
                    <h1 className="text-xl sm:text-2xl font-bold text-foreground mb-2">{companyProfile?.name || 'COMPANY NAME'}</h1>
                    <div className="text-xs sm:text-sm text-gray-600 space-y-1">
                      <p>Address: {companyProfile?.address || 'Company Address'}</p>
                      <p>Mobile: {companyProfile?.phone || '—'} | Email: {companyProfile?.email || '—'}</p>
                      <p>GSTIN: {companyProfile?.gstin || '—'}</p>
                    </div>
                  </div>
                  <div className="text-left sm:text-right w-full sm:w-auto">
                    {companyProfile?.logoUrl ? (
                      <img src={companyProfile.logoUrl} alt="Company" className="h-12 sm:h-16 object-contain ml-auto" />
                    ) : (
                      <div className="text-base sm:text-lg font-semibold text-primary">LOGO</div>
                    )}
                  </div>
                </div>
              </div>

              {/* QUOTE Banner */}
              <div className="bg-foreground text-white text-center py-2 sm:py-3 font-bold text-base sm:text-lg">QUOTE</div>

              {/* Invoice Details + Bill To - 2-col layout matching creation form */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quote No</label>
                    <input
                      type="text"
                      value={editForm.invoice_number}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, invoice_number: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quote Date</label>
                    <div className="relative">
                      <input
                        type="date"
                        value={editForm.invoice_date}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, invoice_date: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                    <div className="relative">
                      <input
                        type="date"
                        value={editForm.due_date}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, due_date: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={editForm.status}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="draft">Draft</option>
                      <option value="sent">Sent</option>
                      <option value="accepted">Accepted</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Type</label>
                    <select
                      value={editContactType}
                      onChange={(e) => {
                        const v = e.target.value;
                        setEditContactType(v);
                        setEditForm((prev) => ({ ...prev, customer: '', customer_name: '', customer_company: '', customer_address: '', customer_gstin: '', customer_pan: '' }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="">Select Type</option>
                      <option value="customer">Customer</option>
                      <option value="client">Client</option>
                    </select>
                  </div>
                  {editContactType && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{editContactType === 'customer' ? 'Select Customer' : 'Select Client'}</label>
                      <select
                        value={editForm.customer}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val && editContactType === 'customer') {
                            const c = editCustomers.find((x) => String(x.id) === val);
                            if (c) {
                              const d = extractCustomerDetails(c);
                              setEditForm((prev) => ({ ...prev, customer: val, customer_name: d.name, customer_company: d.company, customer_address: d.address, customer_gstin: d.gstin, customer_pan: d.pan }));
                            } else setEditForm((prev) => ({ ...prev, customer: val }));
                          } else if (val && editContactType === 'client') {
                            const c = editClients.find((x) => String(x.id) === val);
                            if (c) {
                              const d = extractClientDetails(c);
                              setEditForm((prev) => ({ ...prev, customer: val, customer_name: d.name, customer_company: d.company, customer_address: d.address, customer_gstin: d.gstin, customer_pan: d.pan }));
                            } else setEditForm((prev) => ({ ...prev, customer: val }));
                          } else setEditForm((prev) => ({ ...prev, customer: val, customer_name: '', customer_company: '', customer_address: '', customer_gstin: '', customer_pan: '' }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="">Select</option>
                        {editContactType === 'client'
                          ? editClients.map((c) => (
                              <option key={c.id} value={c.id}>{c.name || c.client_name || c.company_name || `Client ${c.id}`}</option>
                            ))
                          : editCustomers.map((c) => (
                              <option key={c.id} value={c.id}>{getCustomerDisplayName(c)}</option>
                            ))}
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input type="text" value={editForm.customer_name} onChange={(e) => setEditForm((prev) => ({ ...prev, customer_name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Customer/Client name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                    <input type="text" value={editForm.customer_company} onChange={(e) => setEditForm((prev) => ({ ...prev, customer_company: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Company name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <input type="text" value={editForm.customer_address} onChange={(e) => setEditForm((prev) => ({ ...prev, customer_address: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Billing address" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">GSTIN</label>
                      <input type="text" value={editForm.customer_gstin} onChange={(e) => setEditForm((prev) => ({ ...prev, customer_gstin: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" placeholder="GSTIN" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">PAN</label>
                      <input type="text" value={editForm.customer_pan} onChange={(e) => setEditForm((prev) => ({ ...prev, customer_pan: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" placeholder="PAN" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Services Table - matches creation form */}
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-foreground mb-3">Services</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-[640px] sm:min-w-full w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium">S.No</th>
                        <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium">SAC / HSN Code</th>
                        <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium min-w-[150px]">Description of Goods / Services</th>
                        <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium">Quantity</th>
                        <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium">Rate (₹)</th>
                        <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium">Amount (₹)</th>
                        <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium">Tax Rate</th>
                        <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium">Tax Amount</th>
                        <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editForm.items.map((item, idx) => (
                        <tr key={item.id || idx}>
                          <td className="border border-gray-300 px-4 py-2 text-sm">{idx + 1}</td>
                          <td className="border border-gray-300 px-4 py-2">
                            <input type="text" value={item.sac_code || item.hsn_code || ''} onChange={(e) => { const v = e.target.value; setEditForm((prev) => { const items = [...prev.items]; items[idx] = { ...items[idx], sac_code: v, hsn_code: v }; return { ...prev, items }; }); }} className="w-full min-w-[80px] px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary" placeholder="SAC/HSN" />
                          </td>
                          <td className="border border-gray-300 px-4 py-2">
                            <input type="text" value={item.description || ''} onChange={(e) => handleEditItemChange(idx, 'description', e.target.value)} className="w-full min-w-[150px] px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary" placeholder="Description" />
                          </td>
                          <td className="border border-gray-300 px-4 py-2">
                            <input type="number" value={item.quantity ?? ''} onChange={(e) => handleEditItemChange(idx, 'quantity', e.target.value)} min="1" className="w-full min-w-[60px] px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary" />
                          </td>
                          <td className="border border-gray-300 px-4 py-2">
                            <input type="number" value={item.rate ?? ''} onChange={(e) => handleEditItemChange(idx, 'rate', e.target.value)} min="0" step="0.01" className="w-full min-w-[80px] px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary" />
                          </td>
                          <td className="border border-gray-300 px-4 py-2">
                            <input type="number" value={item.amount ?? ''} onChange={(e) => handleEditItemChange(idx, 'amount', e.target.value)} min="0" step="0.01" className="w-full min-w-[80px] px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary" />
                          </td>
                          <td className="border border-gray-300 px-4 py-2">
                            <input type="text" value={item.tax_rate ?? ''} onChange={(e) => handleEditItemChange(idx, 'tax_rate', e.target.value)} className="w-full min-w-[60px] px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary" placeholder="%" />
                          </td>
                          <td className="border border-gray-300 px-4 py-2">
                            <input type="number" value={item.tax_amount ?? ''} onChange={(e) => handleEditItemChange(idx, 'tax_amount', e.target.value)} min="0" step="0.01" className="w-full min-w-[80px] px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary" />
                          </td>
                          <td className="border border-gray-300 px-4 py-2">
                            <button type="button" onClick={() => handleRemoveEditItem(idx)} className="text-red-600 hover:text-red-800 p-1" title="Remove"><Trash2 className="w-4 h-4" /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Button type="button" variant="outline" onClick={handleAddEditItem} className="mt-3 w-full sm:w-auto inline-flex items-center">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Item
                </Button>
              </div>

              {/* Amount Summary - matches creation form */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center gap-2">
                      <span className="text-sm text-gray-700">Total Taxable Value:</span>
                      <input type="number" step="0.01" value={editForm.subtotal} onChange={(e) => setEditForm((prev) => ({ ...prev, subtotal: e.target.value }))} className="text-sm font-semibold w-32 px-2 py-1 border border-gray-200 rounded" />
                    </div>
                    <div className="flex justify-between items-center gap-2">
                      <span className="text-sm text-gray-700">Tax Amount:</span>
                      <input type="number" step="0.01" value={editForm.tax_amount} onChange={(e) => setEditForm((prev) => ({ ...prev, tax_amount: e.target.value }))} className="text-sm font-semibold w-32 px-2 py-1 border border-gray-200 rounded" />
                    </div>
                    <div className="flex justify-between items-center gap-2 pt-2 border-t border-gray-300">
                      <span className="font-semibold">Total Amount:</span>
                      <input type="number" step="0.01" value={editForm.total_amount} onChange={(e) => setEditForm((prev) => ({ ...prev, total_amount: e.target.value }))} className="text-sm font-semibold w-32 px-2 py-1 border border-gray-200 rounded" />
                    </div>
                    <p className="text-xs text-gray-600 pt-2">Total (in words): {numberToWords(Math.floor(parseFloat(editForm.total_amount || 0)))} Rupees Only</p>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Signature</h3>
                  <div className="text-sm text-gray-600">
                    <p>For {companyProfile?.name || 'COMPANY NAME'}</p>
                    <div className="mt-8 border-t border-gray-300 pt-2"><p>Authorized Signatory</p></div>
                  </div>
                </div>
              </div>

              {/* Notes & Terms */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <textarea rows={4} value={editForm.notes || ''} onChange={(e) => setEditForm((prev) => ({ ...prev, notes: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Additional notes..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Terms</label>
                <textarea rows={3} value={editForm.terms || ''} onChange={(e) => setEditForm((prev) => ({ ...prev, terms: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Payment terms, validity period, etc." />
              </div>

              <div className="flex justify-center pt-4 border-t border-gray-200">
                <Button type="button" variant="outline" onClick={() => { setIsEditOpen(false); setEditQuote(null); }}>Cancel</Button>
                <Button type="submit" disabled={isEditLoading} className="ml-3">{isEditLoading ? 'Updating...' : 'Update Quote'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Success Toast */}
      {successMessage && (
        <div className="fixed bottom-4 right-4 z-[100]">
          <div className="bg-green-600 text-white px-4 py-3 rounded-md shadow-lg flex items-center space-x-2">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm">{successMessage}</span>
            <button onClick={() => setSuccessMessage('')} className="text-white/80 hover:text-white text-xs">Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Quotes;

